import { colors, HBBannerBase, HBFileAttachment, HBLinkButton, HBTooltip, IBannerAction } from '@honeybook/hbui';
import { Clip16, ExclamationMarkTriangle24 } from '@honeybook/icons';
import { ApplicationDocument } from '@unit-finance/unit-node-sdk';
import { useTranslation } from 'next-i18next';
import React, { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import ClientOnly from 'components/ClientOnly';
import LocalizedText from 'components/LocalizedText';
import { ApplicationEvents } from 'lib/client/services/mixpanel/mixpanel-events';
import { fileToBase64 } from 'lib/client/utils';
import { DocumentMimeType } from 'lib/types/types';
import { Document } from '@finance/platform/dist/types';
import { LocaleNS } from 'lib/client/locale-ns';
import useAnalytics from 'lib/client/services/mixpanel/useAnalytics';

interface IProps {
    applicationDocument: ApplicationDocument;
    setUploadedDocuments: Dispatch<SetStateAction<Document[]>>;
    entityType: string;
    maxFileSizeInMB: number;
    isUnitError: boolean;
    isBannerDisplayed: boolean;
    setIsBannerDisplayed: Dispatch<SetStateAction<boolean>>;
}

export enum DocumentUploadErrorTypes {
    FileTooBig = 'fileTooBig',
    InvalidDocument = 'invalidDocument'
}

const fileIsTooBig = (file: { size: number }, maxFileSizeInMB: number) => {
    const fileSizeKiloBytes = file.size / 1024 / 1024;

    return fileSizeKiloBytes > maxFileSizeInMB;
};

export function isTesting() {
    return process.env.NODE_ENV === 'test';
}

const ApplicationDocumentAttachment = ({
    applicationDocument,
    setUploadedDocuments,
    entityType,
    maxFileSizeInMB,
    isUnitError,
    isBannerDisplayed,
    setIsBannerDisplayed
}: IProps) => {
    const { t } = useTranslation([LocaleNS.application]);
    const { track } = useAnalytics();
    const [error, setError] = useState<DocumentUploadErrorTypes>();
    const [currentFile, setCurrentFile] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const uploadProgressIntervalRef = useRef(null);

    useEffect(() => {
        if (isUnitError) {
            setError(DocumentUploadErrorTypes.InvalidDocument);
        }
    }, [applicationDocument, isUnitError]);

    // clear progress bar interval on unmount
    useEffect(() => {
        const currentInterval = uploadProgressIntervalRef.current;
        return () => {
            if (currentInterval) {
                clearInterval(currentInterval);
            }
        };
    }, []);

    const hiddenFileInput = useRef<HTMLInputElement>(null);

    const getLocalizedErrorMessage = (errorType: DocumentUploadErrorTypes | undefined) => {
        if (!errorType) return '';
        return t(`documentUpload.errors.${errorType}`);
    };

    const readFile = (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) return;
        track({
            eventName: ApplicationEvents.attachmentClick,
            eventType: 'click',
            properties: {
                type: entityType,
                source: applicationDocument.attributes.documentType
            }
        });

        const file = event.target.files[0];
        if (!file) return;

        setError(undefined);
        setIsBannerDisplayed(false);
        setUploadProgress(0);
        setCurrentFile(file.name);

        if (uploadProgressIntervalRef.current) {
            clearInterval(uploadProgressIntervalRef.current);
        }

        const interval = 20;
        const increment = (100 * interval) / 2000; // Total duration of 2 seconds
        let progress = isTesting() ? 99 : 0;

        const progressInterval = setInterval(() => {
            progress += increment;
            setUploadProgress(progress);
            if (progress >= 100) {
                clearInterval(progressInterval);
                fileToBase64(file).then(fileBase64 => {
                    if (fileIsTooBig(file, maxFileSizeInMB)) {
                        track({
                            eventName: ApplicationEvents.invalidFileSize,
                            eventType: 'error',
                            properties: {
                                type: entityType,
                                source: applicationDocument.attributes.documentType
                            }
                        });
                        setError(DocumentUploadErrorTypes.FileTooBig);
                    } else {
                        setError(undefined);
                        setUploadedDocuments((prevState: Document[]) => {
                            const newFile: Document = {
                                id: applicationDocument.id,
                                file: fileBase64,
                                fileType: file.type as DocumentMimeType,
                                type: applicationDocument.attributes.documentType
                            };

                            return [...prevState, newFile];
                        });
                    }
                });
            }
        }, interval);
    };

    const triggerFileInput = () => hiddenFileInput.current?.click();

    const onRemove = () => {
        setCurrentFile('');
        setError(undefined);
        setUploadedDocuments(prevState => {
            return prevState.filter(doc => doc.id !== applicationDocument.id);
        });
        setIsBannerDisplayed(isUnitError);
        if (hiddenFileInput.current) hiddenFileInput.current.value = '';
    };

    const onRetry = () => {
        track({
            eventName: ApplicationEvents.attachmentClick,
            eventType: 'click',
            properties: {
                type: entityType,
                source: applicationDocument.attributes.documentType
            }
        });
        onRemove();
        triggerFileInput();
    };

    const FileAttachment = () => (
        <HBTooltip
            disabled={!error}
            placement="top"
            targetContainerStyle={{
                display: 'flex',
                height: '42px',
                alignSelf: 'center'
            }}
            offsetY={20}
            maxWidth="217px"
            target={
                <FileContainer data-testid={'file-attachment'}>
                    <HBFileAttachment
                        fileName={currentFile}
                        fileType={'file'}
                        id={applicationDocument.id}
                        testId={'finance-upload-document'}
                        onRemove={onRemove}
                        size={'sm'}
                        uploadError={getLocalizedErrorMessage(error)}
                        canDownload={false}
                        onRetry={onRetry}
                        canPreview={false}
                        uploadProgress={uploadProgress}
                        withBoxShadow={false}
                    />
                </FileContainer>
            }
        >
            {error === DocumentUploadErrorTypes.FileTooBig &&
                t('documentUpload.FileTooBigTooltip', {
                    maxFileSize: maxFileSizeInMB
                })}
        </HBTooltip>
    );

    const AddFileButton = () => (
        <ClientOnly>
            <HBTooltip
                placement="top"
                targetContainerStyle={{
                    margin: '16px 16px 0 0',
                    display: 'block'
                }}
                maxWidth="155px"
                target={
                    <>
                        <HBLinkButton
                            icon={<Clip16 />}
                            linkButtonTheme={'neutral'}
                            testIdName={'attach-document'}
                            size={'sm'}
                            onClick={triggerFileInput}
                        >
                            {t('documentUpload.uploadFile')}
                        </HBLinkButton>
                    </>
                }
            >
                <LocalizedText copyKey="documentUpload.uploadFileTooltip" namespace="application" />
            </HBTooltip>
        </ClientOnly>
    );

    const primaryAction: IBannerAction = {
        text: t('documentUpload.invalidFileBanner.cta'),
        onClick: () => {
            track({
                eventName: ApplicationEvents.attachmentClick,
                eventType: 'click',
                properties: {
                    type: entityType,
                    source: applicationDocument.attributes.documentType
                }
            });
            triggerFileInput();
        }
    };

    const InvalidFileBanner = () => (
        <StyledBaseBanner
            icon={ExclamationMarkTriangle24}
            primaryActionTheme="secondary-solid-secondary"
            accentLine={colors.orange50}
            background="linear-gradient(0deg, rgba(251, 177, 107, 0.08), rgba(251, 177, 107, 0.08)), rgba(255, 255, 255, 0.2)"
            width="100%"
            title={t('documentUpload.invalidFileBanner.title')}
            content={applicationDocument.attributes.reason}
            primaryAction={primaryAction}
            testId="upload-file-error"
        />
    );

    const renderContent = () => {
        if (currentFile) {
            return <FileAttachment />;
        } else if (isBannerDisplayed) {
            return <InvalidFileBanner />;
        } else {
            return <AddFileButton />;
        }
    };

    return (
        <Container isBannerDisplayed={isBannerDisplayed}>
            <input
                type="file"
                name="file"
                ref={hiddenFileInput}
                accept=".pdf,.jpeg,.png"
                onChange={readFile}
                style={{ display: 'none' }}
                data-testid={'file-input'}
            />
            {renderContent()}
        </Container>
    );
};

export default ApplicationDocumentAttachment;

const Container = styled.div<{ isBannerDisplayed: boolean }>`
    display: flex;
    ${props => (props.isBannerDisplayed ? 'padding: 0 20px 16px 20px;' : '')}
`;

const FileContainer = styled.div`
    display: flex;
    align-self: center;
    padding-inline-end: 16px;
`;

const StyledBaseBanner = styled(HBBannerBase)`
    padding: 16px 20px;
`;
